/**
 * Attendance Parser Utility
 * Parses HTML content from the ETC portal attendance page
 * All parsing happens client-side only
 */

import { AttendanceCourse, AttendanceData, ParsedAttendanceRow, AttendanceError } from '@/types/attendance';

// Portal URL constants
export const PORTAL_BASE_URL = 'https://etc.imsciences.edu.pk';
export const PORTAL_LOGIN_URL = PORTAL_BASE_URL; // Main page has login
export const PORTAL_ATTENDANCE_URL = PORTAL_BASE_URL; // Same page - attendance shown after login

/**
 * Parse attendance table from HTML string
 * This function extracts attendance data from the portal's HTML structure
 */
export function parseAttendanceHTML(html: string): ParsedAttendanceRow[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const rows: ParsedAttendanceRow[] = [];

  try {
    // Look for attendance table - adjust selectors based on actual portal structure
    // Common patterns: table with class, table inside specific container
    const tables = doc.querySelectorAll('table');
    
    for (const table of tables) {
      const tableRows = table.querySelectorAll('tbody tr, tr');
      
      for (const row of tableRows) {
        const cells = row.querySelectorAll('td');
        
        // Skip header rows or rows with insufficient data
        if (cells.length < 4) continue;
        
        // Try to extract attendance data
        // Adjust indices based on actual table structure
        const parsed = tryParseRow(cells);
        if (parsed) {
          rows.push(parsed);
        }
      }
    }

    // If no tables found, try alternative structures (divs, lists, etc.)
    if (rows.length === 0) {
      const alternativeRows = tryAlternativeStructures(doc);
      rows.push(...alternativeRows);
    }

  } catch (error) {
    console.error('Error parsing attendance HTML:', error);
  }

  return rows;
}

/**
 * Try to parse a table row into attendance data
 */
function tryParseRow(cells: NodeListOf<Element>): ParsedAttendanceRow | null {
  try {
    // Common table structures:
    // [Course Name, Total, Present, Absent, Percentage]
    // [S.No, Course Name, Course Code, Total, Present, Absent, %]
    
    const cellTexts = Array.from(cells).map(c => c.textContent?.trim() || '');
    
    // Skip if first cell looks like a header
    if (cellTexts[0].toLowerCase().includes('course') || 
        cellTexts[0].toLowerCase().includes('subject') ||
        cellTexts[0].toLowerCase() === 's.no' ||
        cellTexts[0].toLowerCase() === '#') {
      return null;
    }

    // Try different column arrangements
    let courseName = '';
    let courseCode = '';
    let totalClasses = 0;
    let presents = 0;
    let absents = 0;
    let percentage = 0;

    // Pattern 1: [S.No, Course, Code, Total, Present, Absent, %]
    if (cells.length >= 7 && isNumeric(cellTexts[0])) {
      courseName = cellTexts[1];
      courseCode = cellTexts[2];
      totalClasses = parseNumber(cellTexts[3]);
      presents = parseNumber(cellTexts[4]);
      absents = parseNumber(cellTexts[5]);
      percentage = parsePercentage(cellTexts[6]);
    }
    // Pattern 2: [Course, Total, Present, Absent, %]
    else if (cells.length >= 5) {
      courseName = cellTexts[0];
      totalClasses = parseNumber(cellTexts[1]);
      presents = parseNumber(cellTexts[2]);
      absents = parseNumber(cellTexts[3]);
      percentage = parsePercentage(cellTexts[4]);
    }
    // Pattern 3: [S.No, Course, Total, Present, Absent, %]
    else if (cells.length >= 6 && isNumeric(cellTexts[0])) {
      courseName = cellTexts[1];
      totalClasses = parseNumber(cellTexts[2]);
      presents = parseNumber(cellTexts[3]);
      absents = parseNumber(cellTexts[4]);
      percentage = parsePercentage(cellTexts[5]);
    }
    // Pattern 4: [Course, Code, Total, Present, Absent, %]
    else if (cells.length >= 6) {
      courseName = cellTexts[0];
      courseCode = cellTexts[1];
      totalClasses = parseNumber(cellTexts[2]);
      presents = parseNumber(cellTexts[3]);
      absents = parseNumber(cellTexts[4]);
      percentage = parsePercentage(cellTexts[5]);
    }

    // Validate parsed data
    if (!courseName || courseName.length < 2) return null;
    if (totalClasses === 0 && presents === 0 && absents === 0) return null;

    // Calculate percentage if not provided
    if (percentage === 0 && totalClasses > 0) {
      percentage = Math.round((presents / totalClasses) * 100);
    }

    return {
      courseName,
      courseCode: courseCode || undefined,
      totalClasses,
      presents,
      absents,
      percentage
    };
  } catch {
    return null;
  }
}

/**
 * Try alternative HTML structures if tables aren't found
 */
function tryAlternativeStructures(doc: Document): ParsedAttendanceRow[] {
  const rows: ParsedAttendanceRow[] = [];

  // Try card-based layouts
  const cards = doc.querySelectorAll('.card, .course-card, .attendance-card, [class*="attendance"]');
  
  for (const card of cards) {
    const text = card.textContent || '';
    const parsed = tryParseTextBlock(text);
    if (parsed) {
      rows.push(parsed);
    }
  }

  // Try list-based layouts
  const listItems = doc.querySelectorAll('.list-item, .course-item, li[class*="course"]');
  
  for (const item of listItems) {
    const text = item.textContent || '';
    const parsed = tryParseTextBlock(text);
    if (parsed) {
      rows.push(parsed);
    }
  }

  return rows;
}

/**
 * Try to parse attendance from a text block
 */
function tryParseTextBlock(text: string): ParsedAttendanceRow | null {
  try {
    // Look for patterns like "Total: 30", "Present: 25", "Absent: 5", "Attendance: 83%"
    const totalMatch = text.match(/total[:\s]*(\d+)/i);
    const presentMatch = text.match(/present[:\s]*(\d+)/i);
    const absentMatch = text.match(/absent[:\s]*(\d+)/i);
    const percentMatch = text.match(/(\d+(?:\.\d+)?)\s*%/);

    if (!totalMatch && !presentMatch) return null;

    const totalClasses = totalMatch ? parseInt(totalMatch[1]) : 0;
    const presents = presentMatch ? parseInt(presentMatch[1]) : 0;
    const absents = absentMatch ? parseInt(absentMatch[1]) : (totalClasses - presents);
    const percentage = percentMatch ? parseFloat(percentMatch[1]) : 
                       (totalClasses > 0 ? Math.round((presents / totalClasses) * 100) : 0);

    // Try to extract course name (usually at the beginning)
    const lines = text.split('\n').map(l => l.trim()).filter(l => l);
    const courseName = lines[0] || 'Unknown Course';

    return {
      courseName,
      totalClasses,
      presents,
      absents,
      percentage
    };
  } catch {
    return null;
  }
}

/**
 * Extract student info from the page
 */
export function parseStudentInfo(html: string): { name?: string; rollNumber?: string; semester?: string; session?: string } {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  const info: { name?: string; rollNumber?: string; semester?: string; session?: string } = {};

  try {
    // Look for student name
    const namePatterns = [
      /student\s*name[:\s]*([^<\n]+)/i,
      /name[:\s]*([^<\n]+)/i
    ];
    
    const pageText = doc.body?.textContent || '';
    
    for (const pattern of namePatterns) {
      const match = pageText.match(pattern);
      if (match) {
        info.name = match[1].trim();
        break;
      }
    }

    // Look for roll number
    const rollPatterns = [
      /roll\s*(?:no|number)?[:\s]*([A-Z0-9-]+)/i,
      /reg(?:istration)?\s*(?:no|number)?[:\s]*([A-Z0-9-]+)/i
    ];
    
    for (const pattern of rollPatterns) {
      const match = pageText.match(pattern);
      if (match) {
        info.rollNumber = match[1].trim();
        break;
      }
    }

    // Look for semester
    const semesterMatch = pageText.match(/semester[:\s]*(\d+|[IVX]+)/i);
    if (semesterMatch) {
      info.semester = semesterMatch[1];
    }

    // Look for session
    const sessionMatch = pageText.match(/session[:\s]*(\d{4}[-–]\d{2,4})/i);
    if (sessionMatch) {
      info.session = sessionMatch[1];
    }

  } catch (error) {
    console.error('Error parsing student info:', error);
  }

  return info;
}

/**
 * Convert parsed rows to AttendanceData format
 */
export function convertToAttendanceData(
  rows: ParsedAttendanceRow[], 
  studentInfo: { name?: string; rollNumber?: string; semester?: string; session?: string }
): AttendanceData {
  const courses: AttendanceCourse[] = rows.map((row, index) => ({
    id: `course-${index}-${Date.now()}`,
    courseName: row.courseName,
    courseCode: row.courseCode,
    totalClasses: row.totalClasses,
    presents: row.presents,
    absents: row.absents,
    attendancePercentage: row.percentage,
    lastUpdated: new Date()
  }));

  return {
    id: `attendance-${Date.now()}`,
    studentName: studentInfo.name,
    rollNumber: studentInfo.rollNumber,
    semester: studentInfo.semester,
    session: studentInfo.session,
    courses,
    fetchedAt: new Date()
  };
}

/**
 * Validate if the HTML looks like a valid attendance page
 */
export function isValidAttendancePage(html: string): boolean {
  const lowerHtml = html.toLowerCase();
  
  // Check for common attendance-related keywords
  const keywords = ['attendance', 'present', 'absent', 'total', 'subject', 'course', 'classes', 'percentage', '%'];
  const matchCount = keywords.filter(kw => lowerHtml.includes(kw)).length;
  
  // Also check if there's a table (common for attendance data)
  const hasTable = lowerHtml.includes('<table');
  
  // Valid if has at least 2 keywords OR has a table with at least 1 keyword
  return matchCount >= 2 || (hasTable && matchCount >= 1);
}

/**
 * Check if the page indicates a login is required
 */
export function requiresLogin(html: string): boolean {
  const lowerHtml = html.toLowerCase();
  
  // If page has logout link, user is logged in
  if (lowerHtml.includes('logout') || lowerHtml.includes('sign out')) {
    return false;
  }
  
  // If page has attendance data indicators, user is logged in
  if (lowerHtml.includes('present') && lowerHtml.includes('absent')) {
    return false;
  }
  
  // Check for login form indicators
  const hasLoginForm = lowerHtml.includes('captchainputtext') || 
                       (lowerHtml.includes('type="password"') && lowerHtml.includes('username'));
  
  return hasLoginForm;
}

/**
 * Create an error object for attendance parsing issues
 */
export function createParseError(type: AttendanceError['type'], details?: string): AttendanceError {
  const messages: Record<AttendanceError['type'], string> = {
    'login-failed': 'Please log in to the portal first',
    'parse-error': 'Could not read attendance data. The portal structure may have changed.',
    'network-error': 'Could not connect to the portal. Please check your internet connection.',
    'portal-changed': 'The portal structure has changed. Please report this issue.',
    'unknown': 'An unexpected error occurred'
  };

  return {
    type,
    message: messages[type],
    details
  };
}

// Helper functions
function isNumeric(str: string): boolean {
  return /^\d+$/.test(str.trim());
}

function parseNumber(str: string): number {
  const num = parseInt(str.replace(/[^\d]/g, ''), 10);
  return isNaN(num) ? 0 : num;
}

function parsePercentage(str: string): number {
  const match = str.match(/(\d+(?:\.\d+)?)/);
  if (match) {
    return Math.round(parseFloat(match[1]));
  }
  return 0;
}
